import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Save, Download } from "lucide-react";
import { API } from "@/lib/api";

// Per-form field schema. Extend by adding entries — no code changes needed.
const SCHEMAS = {
  "FLA": {
    title: "FLA Return · Foreign Liabilities & Assets",
    eyebrow: "RBI · FEMA · Due 15 July",
    fields: [
      { k: "cin", l: "CIN / LLPIN", w: "1/2" },
      { k: "reporting_currency", l: "Reporting Currency", w: "1/2", default: "INR" },
      { k: "total_paid_up_capital", l: "Total Paid-up Capital (₹)", t: "number", w: "1/3" },
      { k: "foreign_share_capital_pct", l: "Foreign Share Capital %", t: "number", w: "1/3" },
      { k: "total_fdi_inward", l: "Total Inward FDI (₹)", t: "number", w: "1/3" },
      { k: "total_investment_abroad", l: "Total Investment Abroad (₹)", t: "number", w: "1/3" },
      { k: "external_commercial_borrowings", l: "ECBs Outstanding (₹)", t: "number", w: "1/3" },
      { k: "trade_credits", l: "Trade Credits Outstanding (₹)", t: "number", w: "1/3" },
    ],
  },
  "DIR-3-KYC": {
    title: "DIR-3 KYC · Director KYC",
    eyebrow: "Companies Act · Due 30 September",
    fields: [
      { k: "din", l: "DIN", w: "1/3" },
      { k: "director_name", l: "Full Name", w: "1/3" },
      { k: "father_name", l: "Father's Name", w: "1/3" },
      { k: "dob", l: "Date of Birth", t: "date", w: "1/3" },
      { k: "nationality", l: "Nationality", w: "1/3", default: "Indian" },
      { k: "passport_no", l: "Passport No. (if any)", w: "1/3" },
      { k: "email", l: "Email", w: "1/2" },
      { k: "mobile", l: "Mobile", w: "1/2" },
      { k: "permanent_address", l: "Permanent Address", t: "textarea", w: "full" },
      { k: "present_address", l: "Present Address", t: "textarea", w: "full" },
    ],
  },
  "MSME-1": {
    title: "MSME-1 · Outstanding to MSMEs > 45 days",
    eyebrow: "Companies Act · Half-yearly (Apr / Oct)",
    fields: [
      { k: "half_year", l: "Half Year", w: "1/3", default: "Oct-Mar" },
      { k: "no_of_msme_vendors", l: "MSME Vendors Count", t: "number", w: "1/3" },
      { k: "total_outstanding", l: "Total Outstanding > 45 days (₹)", t: "number", w: "1/3" },
      { k: "reason_for_delay", l: "Reason for Delay", t: "textarea", w: "full" },
    ],
  },
  "BEN-2": {
    title: "BEN-2 · Return of Significant Beneficial Ownership",
    eyebrow: "Companies Act · Filed within 30 days of BEN-1",
    fields: [
      { k: "cin", l: "CIN", w: "1/2" },
      { k: "sbo_name", l: "Significant Beneficial Owner", w: "1/2" },
      { k: "sbo_pan", l: "SBO PAN", w: "1/3" },
      { k: "sbo_dob", l: "SBO DOB", t: "date", w: "1/3" },
      { k: "sbo_nationality", l: "Nationality", w: "1/3", default: "Indian" },
      { k: "sbo_holding_pct", l: "Holding %", t: "number", w: "1/2" },
      { k: "date_of_acquisition", l: "Date of Significant Holding", t: "date", w: "1/2" },
    ],
  },
  "ADT-1": {
    title: "ADT-1 · Auditor Appointment Intimation",
    eyebrow: "Companies Act · Within 15 days of AGM",
    fields: [
      { k: "auditor_name", l: "Auditor Name", w: "1/2" },
      { k: "auditor_pan", l: "Auditor PAN", w: "1/2" },
      { k: "firm_name", l: "Firm Name", w: "1/2" },
      { k: "frn", l: "FRN", w: "1/2" },
      { k: "appointment_date", l: "Date of Appointment", t: "date", w: "1/3" },
      { k: "term_from_fy", l: "Appointed From FY", w: "1/3" },
      { k: "term_to_fy", l: "Appointed Until FY", w: "1/3" },
      { k: "agm_date", l: "AGM Date", t: "date", w: "1/2" },
    ],
  },
  "INC-20A": {
    title: "INC-20A · Commencement of Business",
    eyebrow: "Companies Act · Within 180 days of incorporation",
    fields: [
      { k: "cin", l: "CIN", w: "1/2" },
      { k: "date_of_incorporation", l: "Date of Incorporation", t: "date", w: "1/2" },
      { k: "commencement_date", l: "Date of Commencement", t: "date", w: "1/2" },
      { k: "subscribed_capital_paid", l: "Subscribed Capital Paid by Subscribers (₹)", t: "number", w: "1/2" },
      { k: "bank_name", l: "Bank Name", w: "1/2" },
      { k: "bank_account_no", l: "Bank Account No.", w: "1/2" },
    ],
  },
  "PAS-6": {
    title: "PAS-6 · Reconciliation of Share Capital",
    eyebrow: "Companies Act · Half-yearly (unlisted PLC)",
    fields: [
      { k: "half_year", l: "Half Year", w: "1/3", default: "H1" },
      { k: "issued_capital", l: "Total Issued Capital (₹)", t: "number", w: "1/3" },
      { k: "capital_in_demat", l: "Capital held in Demat (₹)", t: "number", w: "1/3" },
      { k: "capital_in_physical", l: "Capital held in Physical (₹)", t: "number", w: "1/3" },
      { k: "shareholders_demat", l: "Shareholders in Demat", t: "number", w: "1/3" },
      { k: "shareholders_physical", l: "Shareholders in Physical", t: "number", w: "1/3" },
      { k: "discrepancy_reason", l: "Reason for any discrepancy", t: "textarea", w: "full" },
    ],
  },
  "LLP-11": {
    title: "LLP Form 11 · Annual Return",
    eyebrow: "LLP Act · Due 30 May annually",
    fields: [
      { k: "llpin", l: "LLPIN", w: "1/3" },
      { k: "business_classification", l: "Business Classification", w: "1/3" },
      { k: "total_partners", l: "Total Partners", t: "number", w: "1/3" },
      { k: "designated_partners", l: "Designated Partners", t: "number", w: "1/3" },
      { k: "total_contribution", l: "Total Contribution Received (₹)", t: "number", w: "1/3" },
      { k: "contribution_obligation", l: "Obligation of Contribution (₹)", t: "number", w: "1/3" },
      { k: "penalty_details", l: "Penalty / Compounding Offences (if any)", t: "textarea", w: "full" },
    ],
  },
};

export default function GenericMCAWizard() {
  const { formCode } = useParams();
  const schema = SCHEMAS[formCode] || { title: formCode, fields: [] };
  const [fy, setFy] = useState("2025-26");
  const [d, setD] = useState({ fy: "2025-26" });

  useEffect(() => { (async () => {
    const r = await api.get(`/mca/generic/${formCode}`, { params: { fy } });
    const withDefaults = { ...d, fy };
    schema.fields.forEach((f) => { if (f.default && !r.data[f.k]) withDefaults[f.k] = f.default; });
    setD({ ...withDefaults, ...r.data });
  })(); /* eslint-disable-next-line */ }, [fy, formCode]);

  const save = async () => { try { await api.post(`/mca/generic/${formCode}`, d); toast.success(`${formCode} saved`); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const exp = async () => {
    const res = await fetch(`${API}/mca/generic/${formCode}.json?fy=${fy}`, { credentials: "include" });
    if (!res.ok) return toast.error("Save first");
    const blob = await res.blob(); const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = `${formCode}_${fy}.json`; a.click(); URL.revokeObjectURL(u);
  };

  const widthCls = (w) => ({ "1/2": "md:col-span-3", "1/3": "md:col-span-2", "1/4": "md:col-span-1", "full": "md:col-span-6" }[w] || "md:col-span-2");

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid={`${formCode.toLowerCase()}-page`}>
      <PageHeader eyebrow={schema.eyebrow || "Corporate Compliance"} title={schema.title}
        description={`Draft persists per FY on the server. Export MCA-compatible JSON when ready.`} />
      <Card className="p-4 rounded-sm flex items-end gap-3 flex-wrap">
        <div><Label>Financial Year</Label><Input value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-sm mt-1 w-40" data-testid="fy" /></div>
        <div className="flex-1" />
        <Button variant="outline" className="rounded-sm gap-1.5" onClick={save} data-testid="save"><Save className="w-4 h-4" /> Save Draft</Button>
        <Button className="rounded-sm bg-accent text-white gap-1.5" onClick={exp} data-testid="export"><Download className="w-4 h-4" /> Export JSON</Button>
      </Card>
      <Card className="p-5 rounded-sm grid md:grid-cols-6 gap-3">
        {schema.fields.map((f) => (
          <div key={f.k} className={widthCls(f.w)}>
            <Label>{f.l}</Label>
            {f.t === "textarea" ? (
              <Textarea value={d[f.k] || ""} onChange={(e) => setD({ ...d, [f.k]: e.target.value })} className="rounded-sm mt-1 min-h-[60px]" data-testid={`fld-${f.k}`} />
            ) : (
              <Input type={f.t || "text"} value={d[f.k] ?? ""} onChange={(e) => setD({ ...d, [f.k]: f.t === "number" ? Number(e.target.value) : e.target.value })}
                     className={`rounded-sm mt-1 ${f.t === "number" ? "text-right tabular-nums" : ""}`} data-testid={`fld-${f.k}`} />
            )}
          </div>
        ))}
      </Card>
    </div>
  );
}
